import { z } from 'zod';
import { isRecurrenceFrequency, type RecurrenceFrequency } from '@/lib/recurrence';
import type { CreateSeriesInput } from '@/hooks/useRecurringSeries';

/** "Repeats" choices in job/enquiry forms. 'none' = Doesn't repeat. */
export type RepeatChoice = 'none' | RecurrenceFrequency;
export type RepeatEndMode = 'never' | 'on_date' | 'after';

export interface RepeatSettings {
  frequency: RepeatChoice;
  endMode: RepeatEndMode;
  endDate: string;
  count: string;
}

export const DEFAULT_REPEAT_SETTINGS: RepeatSettings = {
  frequency: 'none',
  endMode: 'never',
  endDate: '',
  count: '6',
};

/** Validates the repeat block against the first visit's date. */
export const repeatSettingsSchema = (firstVisit: string) =>
  z
    .object({
      frequency: z.enum(['none', 'weekly', 'fortnightly', 'four_weekly', 'monthly']),
      endMode: z.enum(['never', 'on_date', 'after']),
      endDate: z.string(),
      count: z.string(),
    })
    .superRefine((v, ctx) => {
      if (v.frequency === 'none') return;
      if (v.endMode === 'on_date') {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(v.endDate)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endDate'], message: 'Choose the date the repeat clean ends.' });
        } else if (firstVisit && v.endDate < firstVisit) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['endDate'], message: 'The end date must be on or after the first visit.' });
        }
      }
      if (v.endMode === 'after') {
        const n = Number(v.count);
        if (!Number.isInteger(n) || n < 2 || n > 520) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['count'], message: 'Enter between 2 and 520 visits.' });
        }
      }
    });

/** First validation message, or null when the settings are fine. */
export function repeatSettingsError(settings: RepeatSettings, firstVisit: string): string | null {
  const result = repeatSettingsSchema(firstVisit).safeParse(settings);
  return result.success ? null : result.error.issues[0]?.message ?? 'Check the repeat settings.';
}

/** RPC input for create_recurring_series_from_job, or null for "Doesn't repeat". */
export function toCreateSeriesInput(jobId: string, settings: RepeatSettings): CreateSeriesInput | null {
  if (!isRecurrenceFrequency(settings.frequency)) return null;
  return {
    jobId,
    frequency: settings.frequency,
    endDate: settings.endMode === 'on_date' ? settings.endDate : null,
    occurrenceCount: settings.endMode === 'after' ? Number(settings.count) : null,
  };
}
